import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Card,
  CardActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useDocs } from "../../../../hooks/useDocs";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

const StepThree = ({ formData, setFormData }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [librariesFromDB, setLibrariesFromDB] = useState([]);
  const [selectedLibraryId, setSelectedLibraryId] = useState("");
  const [editBuffer, setEditBuffer] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const { getAllLibraries } = useDocs();

  const library = formData.content?.library || [];

  // ====== FETCH EXISTING LIBRARIES ======
  useEffect(() => {
    const fetchLibraries = async () => {
      const libs = await getAllLibraries();
      setLibrariesFromDB(libs || []);
    };
    fetchLibraries();
  }, [getAllLibraries]);

  // ====== Initialize empty array ======
  useEffect(() => {
    if (!formData.content?.library || formData.content.library.length === 0) {
      setFormData((prev) => ({
        ...prev,
        content: { ...prev.content, library: [] },
      }));
    }
  }, [formData, setFormData]);

  // ====== Handle Add ======
  const handleAdd = () => {
    const newItem = {
      id: Date.now().toString(),
      type: "",
      name: "",
      url: "",
      image: "",
      video: "",
      description: "",
    };
    const updated = [...library, newItem];
    setFormData((prev) => ({
      ...prev,
      content: { ...prev.content, library: updated },
    }));
    setEditingIndex(updated.length - 1);
    setEditBuffer(newItem);
  };

  // ====== Handle Edit ======
  const openEditor = (idx) => {
    setEditingIndex(idx);
    setEditBuffer({ ...library[idx] });
    setSelectedLibraryId(library[idx].id);
  };

  // ====== Trigger Delete Confirmation ======
  const requestDelete = (idx) => {
    setDeleteIndex(idx);
    setOpenConfirm(true);
  };

  // ====== Confirm Delete ======
  const handleConfirmDelete = () => {
    if (deleteIndex === null) return;
    const updated = library.filter((_, i) => i !== deleteIndex);
    setFormData((prev) => ({
      ...prev,
      content: { ...prev.content, library: updated },
    }));
    if (editingIndex === deleteIndex) setEditingIndex(null);
    setDeleteIndex(null);
    setOpenConfirm(false);
  };

  // ====== Cancel Delete ======
  const handleCancelDelete = () => {
    setDeleteIndex(null);
    setOpenConfirm(false);
  };

  // ====== Handle Library Select from DB ======
  const handleLibrarySelect = (libraryId) => {
    setSelectedLibraryId(libraryId);
    const selected = librariesFromDB.find((lib) => lib.id === libraryId);
    if (selected) setEditBuffer({ ...selected });
  };

  // ====== Handle Field Changes ======
  const handleEditChange = (field, value) => {
    setEditBuffer((prev) => ({ ...prev, [field]: value }));
  };

  // ====== Handle Save ======
  const handleSave = () => {
    if (editingIndex === null || !editBuffer) return;
    const updated = [...library];
    updated[editingIndex] = { ...editBuffer };
    setFormData((prev) => ({
      ...prev,
      content: { ...prev.content, library: updated },
    }));
    setEditingIndex(null);
    setEditBuffer(null);
  };

  // ====== Handle Back ======
  const handleBack = () => {
    // If adding a new empty item and user backs out → remove it
    if (editingIndex === library.length - 1 && !library[editingIndex]?.name) {
      const updated = library.slice(0, -1);
      setFormData((prev) => ({
        ...prev,
        content: { ...prev.content, library: updated },
      }));
    }

    setEditingIndex(null);
    setEditBuffer(null);
    setSelectedLibraryId("");
  };

  // ====== Handle Drag Reordering ======
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(library);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setFormData((prev) => ({
      ...prev,
      content: { ...prev.content, library: reordered },
    }));
  };

  // ====== Handle Move Up ======
  const moveItemUp = (index) => {
    if (index === 0) return; // already at top
    const updated = [...library];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]]; // swap
    setFormData((prev) => ({
      ...prev,
      content: { ...prev.content, library: updated },
    }));
  };

  // ====== Handle Move Down ======
  const moveItemDown = (index) => {
    if (index === library.length - 1) return; // already at bottom
    const updated = [...library];
    [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]]; // swap
    setFormData((prev) => ({
      ...prev,
      content: { ...prev.content, library: updated },
    }));
  };

  // ====== UI ======
  return (
    <Box className="mt-6">
      <Typography variant="h6" className="mb-3 font-semibold">
        Library Items
      </Typography>

      {editingIndex !== null && editBuffer ? (
        <Box className="border border-gray-300 p-4 rounded-lg mb-4 bg-white shadow-sm">
          <Box className="flex items-center justify-between mb-3">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={handleBack}
            >
              <ArrowBackIcon fontSize="small" />
              <span className="text-sm font-medium">Back</span>
            </div>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              className="!capitalize"
            >
              Save
            </Button>
          </Box>

          <FormControl fullWidth className="mb-3">
            <InputLabel>Select Existing Library</InputLabel>
            <Select
              label="Select Existing Library"
              value={selectedLibraryId}
              onChange={(e) => handleLibrarySelect(e.target.value)}
            >
              <MenuItem value="">
                <em>Select a library</em>
              </MenuItem>
              {librariesFromDB.map((lib) => (
                <MenuItem key={lib.id} value={lib.id}>
                  {lib.name || `Library ${lib.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {["name", "description", "type", "url", "image", "video"].map(
            (field) => (
              <TextField
                key={field}
                label={field.charAt(0).toUpperCase() + field.slice(1)}
                value={editBuffer[field] || ""}
                onChange={(e) => handleEditChange(field, e.target.value)}
                fullWidth
                className="!my-2"
              />
            )
          )}
        </Box>
      ) : (
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="library">
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps}>
                  {library.map((item, idx) => (
                    <Draggable key={item.id} draggableId={item.id} index={idx}>
                      {(provided) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="flex justify-between items-center mb-2 px-4 py-2 rounded-lg shadow-sm border border-gray-200 bg-white"
                        >
                          <Box
                            {...provided.dragHandleProps}
                            className="flex items-center gap-2 cursor-grab"
                          >
                            <DragIndicatorIcon />
                            <Typography>{item?.name}</Typography>
                          </Box>
                          <CardActions>
                            <IconButton
                              size="small"
                              onClick={() => moveItemUp(idx)}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveItemDown(idx)}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton onClick={() => openEditor(idx)}>
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              onClick={() => requestDelete(idx)}
                              disabled={library.length === 0}
                            >
                              <DeleteIcon color="error" />
                            </IconButton>
                          </CardActions>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>

          <Button
            variant="outlined"
            onClick={handleAdd}
            className="mt-2 border-gray-400 text-gray-700"
          >
            + Add Library Item
          </Button>
        </>
      )}

      {/* ====== CONFIRMATION MODAL ====== */}
      <ConfirmationModal
        isOpened={openConfirm}
        handleCancle={handleCancelDelete}
        handleConfirmation={handleConfirmDelete}
        title="Confirm Deletion"
        description="Are you sure you want to delete this library item?"
        buttonTitle="Delete"
      />
    </Box>
  );
};

export default StepThree;
